// src/components/Uber/DeliveryOptions.jsx
import React, { useEffect, useState } from "react";
import { useConfig } from "../../context/ConfigContext";
import axios from "../../utils/axiosHandler";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

const DeliveryOptions = ({
  selectedDeliveryMethod,
  setSelectedDeliveryMethod,
  deliveryCost,
  setDeliveryCost,
  userLocation,
  estimatedDeliveryTime,
  setEstimatedDeliveryTime,
}) => {
  const { config } = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryEstimate, setDeliveryEstimate] = useState(null);
  const [error, setError] = useState(null);

  // Check delivery availability and estimated time when method changes
  useEffect(() => {
    if (selectedDeliveryMethod === "delivery") {
      checkDeliveryAvailability();
    }
  }, [selectedDeliveryMethod, userLocation]);

  const checkDeliveryAvailability = async () => {
    if (!userLocation || !userLocation.postalCode) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call backend to check delivery availability with Uber
      const response = await axios.post("/api/check-delivery", {
        destination: userLocation,
      });

      if (response.data.available) {
        setDeliveryCost(response.data.cost);
        setEstimatedDeliveryTime(response.data.estimatedTime);
        setDeliveryEstimate({
          minTime: response.data.estimatedTime.min,
          maxTime: response.data.estimatedTime.max,
          cost: response.data.cost,
        });
      } else {
        setError("Delivery is not available to your location");
        setSelectedDeliveryMethod("pickup");
      }
    } catch (err) {
      console.error("Error checking delivery availability:", err);
      setError(
        "Unable to check delivery availability. Please try again or choose pickup."
      );
      // Default to pickup if there's an error
      setSelectedDeliveryMethod("pickup");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Delivery Options
      </h3>

      <RadioGroup
        value={selectedDeliveryMethod}
        onValueChange={setSelectedDeliveryMethod}
        className="space-y-4"
      >
        <div className="flex items-start space-x-3 p-4 border rounded-md transition-all duration-200 hover:border-blue-300 hover:bg-gray-50">
          <RadioGroupItem value="pickup" id="pickup" className="mt-1" />
          <div className="flex-1">
            <Label
              htmlFor="pickup"
              className="font-medium text-gray-700 cursor-pointer flex flex-col"
            >
              <span className="text-gray-900">Pickup</span>
              <span className="text-gray-600 text-sm">
                Ready in 15-30 minutes
              </span>
              <span className="text-gray-600 text-sm">No extra charge</span>
            </Label>
          </div>
        </div>

        <div
          className={`flex items-start space-x-3 p-4 border rounded-md transition-all duration-200 ${selectedDeliveryMethod === "delivery"
              ? "border-blue-500 bg-blue-50"
              : "hover:border-blue-300 hover:bg-gray-50"
            }`}
        >
          <RadioGroupItem value="delivery" id="delivery" className="mt-1" />
          <div className="flex-1">
            <Label
              htmlFor="delivery"
              className="font-medium text-gray-700 cursor-pointer flex flex-col"
            >
              <span className="text-gray-900">Delivery via Uber</span>
              {isLoading ? (
                <span className="text-gray-600 text-sm">
                  Checking availability...
                </span>
              ) : error ? (
                <span className="text-red-600 text-sm">{error}</span>
              ) : deliveryEstimate ? (
                <>
                  <span className="text-gray-600 text-sm">
                    Estimated delivery: {deliveryEstimate.minTime}-
                    {deliveryEstimate.maxTime} minutes
                  </span>
                  <span
                    className="text-gray-600 text-sm"
                    style={{ color: config.colors.primary }}
                  >
                    Delivery fee: ${deliveryEstimate.cost.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-gray-600 text-sm">
                  Fast delivery to your door
                </span>
              )}
            </Label>
          </div>
        </div>
      </RadioGroup>
    </div>
  );
};

export default DeliveryOptions;
